import { Box, Button, Heading, Text, VStack, useToast } from "@chakra-ui/react";
import { useQueryClient } from "react-query";
import { useForm } from "react-hook-form";
import { showErrorToast } from "utils";
import useAirUserRoles from "../../../../utils/AuthUtils";
import { foundationV2Keys, useSetFoundationV2Controllers } from "hooks/query";
import FormSelectUser from "../../FormSelectUser";
import SecondaryHeader from "../../components/SecondaryHeader";

/**
 * First-run gate: the v2 round is created without controllers, so this blocks the page until one is set.
 *
 * A v2-specific copy of v1's setup screen rather than a shared component, because it writes to the v2
 * round's controllers through the v2 endpoint — sharing it would mean one screen mutating whichever round
 * a prop happened to point at.
 */
export default function FoundationV2SetupController() {
  const { isOrgAdmin } = useAirUserRoles();
  const queryClient = useQueryClient();
  const toast = useToast();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const setControllers = useSetFoundationV2Controllers({
    onSuccess: () => queryClient.invalidateQueries(foundationV2Keys.round()),
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
  });

  const onSubmit = (data) => setControllers.mutate(data._owners.map((o) => o.value));

  return (
    <SecondaryHeader showRoundSelector={false} showActions={false}>
      <Box px={4} py={20}>
        <VStack spacing={6} maxW="500px" mx="auto">
          {isOrgAdmin ? (
            <>
              <Heading fontSize="heading.l" color="darkTeal" textAlign="center">
                Set up Foundation Data
              </Heading>
              <Text color="gray.600" textAlign="center">
                Assign a controller to manage Foundation Data. The controller can add reporting years,
                run extractions, and assign owners for each year.
              </Text>
              <Box w="100%">
                <FormSelectUser
                  label="Controllers"
                  name="_owners"
                  control={control}
                  rules={{ required: "A Controller is required" }}
                  size="lg"
                  errors={errors}
                  menuPortalTarget={document.body}
                />
              </Box>
              <Button
                w="100%"
                variant="darkTeal"
                isLoading={setControllers.isLoading}
                onClick={handleSubmit(onSubmit)}
              >
                Save and Continue
              </Button>
            </>
          ) : (
            <>
              <Heading fontSize="heading.l" color="darkTeal" textAlign="center">
                Foundation Data Setup Required
              </Heading>
              <Text color="gray.600" textAlign="center">
                Contact your organization admin to complete the Foundation Data setup.
              </Text>
            </>
          )}
        </VStack>
      </Box>
    </SecondaryHeader>
  );
}
